import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pushSupported, pushPermission, getPushSubscription, unsubscribeFromPush } from "@/lib/push";

const Row = ({ k, v }) => (
  <div className="flex items-start gap-3 py-1 border-b border-zinc-800">
    <div className="text-zinc-500 text-xs font-mono w-44 flex-shrink-0">{k}</div>
    <div className={`text-xs font-mono break-all ${v === true ? "text-green-400" : v === false ? "text-red-400" : "text-zinc-300"}`}>
      {v === true ? "✅ true" : v === false ? "❌ false" : (v ?? "—")}
    </div>
  </div>
);

/**
 * Hidden diagnostic page — accessed via direct URL /push-debug.
 * Restricted to admin, moderator, and a small allow-list of test accounts.
 */
export default function PushDebug() {
  const { user, loading } = useAuth();
  const [state, setState] = useState({});
  const [backend, setBackend] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const addLog = (msg, type = "info") => {
    setLog((l) => [...l, { t: new Date().toLocaleTimeString(), msg, type }]);
  };

  const refresh = async () => {
    setBusy(true);
    const s = {};
    try {
      s.pushSupported = pushSupported();
      s.notificationAPI = "Notification" in window;
      s.serviceWorkerAPI = "serviceWorker" in navigator;
      s.pushManagerAPI = "PushManager" in window;
      s.permission = pushPermission();
      s.token = !!localStorage.getItem("wb_token");
      s.userAgent = navigator.userAgent.slice(0, 80);
      s.location = window.location.href;
      s.backendUrl = process.env.REACT_APP_BACKEND_URL;

      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        s.serviceWorkerRegistered = !!reg;
        if (reg) {
          s.serviceWorkerScope = reg.scope;
          s.serviceWorkerActive = !!reg.active;
        }
      }

      const sub = await getPushSubscription();
      s.hasLocalSubscription = !!sub;
      if (sub) {
        s.localEndpointShort = sub.endpoint.slice(0, 60) + "...";
      }

      try {
        const r = await api.get("/push/vapid-public-key");
        s.vapidConfigured = true;
        s.vapidKeyShort = r.data.public_key.slice(0, 30) + "...";
      } catch (e) {
        s.vapidConfigured = false;
        s.vapidError = (e?.response?.status || "") + " " + (e?.message || "");
      }

      // Backend diagnostic — what does the backend actually see for this user?
      try {
        const r = await api.get("/push/me-debug");
        setBackend(r.data);
      } catch (e) {
        setBackend({ error: (e?.response?.status || "") + " " + (e?.message || "") });
      }
    } catch (e) {
      addLog("Refresh error: " + e.message, "error");
    }
    setState(s);
    setBusy(false);
  };

  useEffect(() => { refresh(); }, []);

  // Access control: admin, moderator (Vedúcko), or whitelisted test accounts only.
  // Non-test users get redirected — keeps internal diagnostics out of public view.
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const allowEmails = ["dragan", "veducko", "admin"]; // any email containing these substrings (case-insensitive)
  const role = (user.role || "").toLowerCase();
  const email = (user.email || "").toLowerCase();
  const name = (user.name || "").toLowerCase();
  const isAllowed =
    role === "admin" ||
    role === "moderator" ||
    allowEmails.some((k) => email.includes(k) || name.includes(k));
  if (!isAllowed) return <Navigate to="/" replace />;

  const handleSubscribe = async () => {
    setBusy(true);
    addLog("Calling subscribeToPush()...", "info");
    try {
      // Inline the subscribe flow so we can log every HTTP response detail.
      // This duplicates lib/push.js intentionally to surface errors that
      // the wrapped helper would otherwise swallow.
      if (!pushSupported()) throw new Error("Push not supported");
      const perm = await Notification.requestPermission();
      addLog("Notification.requestPermission() → " + perm, "info");
      if (perm !== "granted") throw new Error("Permission denied: " + perm);

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      addLog("Existing local sub: " + (sub ? "yes (will reuse)" : "no (will create)"), "info");

      if (!sub) {
        const { data } = await api.get("/push/vapid-public-key");
        const u8 = (function urlBase64ToUint8Array(base64) {
          const padding = "=".repeat((4 - (base64.length % 4)) % 4);
          const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
          const raw = atob(b64);
          const out = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
          return out;
        })(data.public_key);
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: u8 });
        addLog("pushManager.subscribe() succeeded", "success");
      }

      const subJson = sub.toJSON();
      addLog("POSTing endpoint=" + (subJson.endpoint || "").slice(0, 60) + "...", "info");
      const resp = await api.post("/push/subscribe", {
        subscription: subJson,
        user_agent: navigator.userAgent,
      });
      addLog("POST /push/subscribe → " + resp.status + " " + JSON.stringify(resp.data), "success");
    } catch (e) {
      const detail = e?.response?.data ? JSON.stringify(e.response.data) : (e?.message || JSON.stringify(e));
      addLog("subscribeToPush() FAILED: " + (e?.response?.status || "") + " " + detail, "error");
      console.error("subscribeToPush error:", e);
    }
    await refresh();
    setBusy(false);
  };

  const handleUnsubscribe = async () => {
    setBusy(true);
    addLog("Calling unsubscribeFromPush()...", "info");
    try {
      await unsubscribeFromPush();
      addLog("unsubscribeFromPush() succeeded", "success");
    } catch (e) {
      addLog("unsubscribeFromPush() FAILED: " + (e?.message || ""), "error");
    }
    await refresh();
    setBusy(false);
  };

  const handleTest = async () => {
    setBusy(true);
    addLog("Calling /api/push/test...", "info");
    try {
      const r = await api.post("/push/test");
      addLog("Test push API returned: delivered=" + (r.data?.delivered ?? "?"), "success");
      if (r.data?.delivered === 0) {
        addLog("delivered=0 — backend has NO subscription for this user. Click Subscribe first!", "warn");
      } else {
        addLog("Push payload sent — notification should arrive in 1-5 sec. If not, check Android battery optimization for Chrome.", "info");
      }
    } catch (e) {
      addLog("/push/test FAILED: " + (e?.response?.status || "") + " " + (e?.message || ""), "error");
    }
    setBusy(false);
  };

  const handleClearLocal = () => {
    localStorage.removeItem("tigik_push_onboard_v1");
    addLog("Cleared local PushOnboardingModal state.", "info");
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6 max-w-2xl mx-auto" data-testid="push-debug-page">
      <h1 className="text-xl font-black mb-1">Push Debug</h1>
      <p className="text-xs text-zinc-500 mb-6">Diagnostika push notifikácií — krok-za-krokom test pipeline.</p>

      <div className="bg-zinc-950 border border-amber-700 p-4 mb-4">
        <h2 className="text-sm font-bold text-amber-400 mb-2">Backend view (čo o tebe vie server)</h2>
        {!backend && <p className="text-xs text-zinc-500">Loading…</p>}
        {backend?.error && <p className="text-xs text-red-400 font-mono break-all">Error: {backend.error}</p>}
        {backend && !backend.error && (
          <>
            <Row k="user_id (z JWT)" v={backend.user_id} />
            <Row k="user_email" v={backend.user_email} />
            <Row k="user_name" v={backend.user_name} />
            <Row k="user_role" v={backend.user_role} />
            <Row k="subscription_count (server)" v={String(backend.subscription_count)} />
            {(backend.subscriptions || []).map((s, i) => (
              <div key={i} className="mt-2 text-[10px] font-mono text-zinc-400 break-all border-l-2 border-amber-700 pl-2">
                <div className="text-amber-300">#{i + 1} endpoint:</div>
                <div>{s.endpoint_preview}</div>
                <div className="text-zinc-500">UA: {s.user_agent}</div>
                <div className="text-zinc-500">created: {s.created_at}</div>
                <div className="text-zinc-500">updated: {s.updated_at}</div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="bg-zinc-950 border border-zinc-800 p-4 mb-4">
        <h2 className="text-sm font-bold text-gold-400 mb-2">Status (čo browser hovorí)</h2>
        <Row k="pushSupported()" v={state.pushSupported} />
        <Row k="Notification API" v={state.notificationAPI} />
        <Row k="ServiceWorker API" v={state.serviceWorkerAPI} />
        <Row k="PushManager API" v={state.pushManagerAPI} />
        <Row k="Notification permission" v={state.permission} />
        <Row k="Logged in (wb_token)" v={state.token} />
        <Row k="ServiceWorker registered" v={state.serviceWorkerRegistered} />
        <Row k="ServiceWorker active" v={state.serviceWorkerActive} />
        <Row k="ServiceWorker scope" v={state.serviceWorkerScope} />
        <Row k="Local subscription exists" v={state.hasLocalSubscription} />
        <Row k="Local endpoint (first 60ch)" v={state.localEndpointShort} />
        <Row k="Backend VAPID configured" v={state.vapidConfigured} />
        <Row k="VAPID public key" v={state.vapidKeyShort} />
        <Row k="VAPID error" v={state.vapidError} />
        <Row k="Backend URL" v={state.backendUrl} />
        <Row k="UA" v={state.userAgent} />
        <Row k="Current URL" v={state.location} />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 p-4 mb-4">
        <h2 className="text-sm font-bold text-gold-400 mb-3">Actions</h2>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="debug-subscribe" disabled={busy} onClick={handleSubscribe}
            className="bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white text-xs font-bold py-2 px-3">
            1. Subscribe
          </button>
          <button data-testid="debug-test-push" disabled={busy} onClick={handleTest}
            className="bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white text-xs font-bold py-2 px-3">
            2. Send test push
          </button>
          <button data-testid="debug-unsubscribe" disabled={busy} onClick={handleUnsubscribe}
            className="bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white text-xs font-bold py-2 px-3">
            Unsubscribe
          </button>
          <button data-testid="debug-clear" disabled={busy} onClick={handleClearLocal}
            className="bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white text-xs font-bold py-2 px-3">
            Clear local state
          </button>
          <button data-testid="debug-refresh" disabled={busy} onClick={refresh}
            className="bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white text-xs font-bold py-2 px-3 col-span-2">
            Refresh status
          </button>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 p-4">
        <h2 className="text-sm font-bold text-gold-400 mb-2">Log</h2>
        {log.length === 0 ? (
          <p className="text-xs text-zinc-600">No actions yet. Click Subscribe then Send test push.</p>
        ) : (
          <div className="space-y-1">
            {log.map((l, i) => (
              <div key={i} className={`text-xs font-mono ${l.type === "error" ? "text-red-400" : l.type === "success" ? "text-green-400" : l.type === "warn" ? "text-yellow-400" : "text-zinc-400"}`}>
                <span className="text-zinc-600">[{l.t}]</span> {l.msg}
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[10px] text-zinc-700 text-center mt-6">
        Tigik Push Debug · v1 · Pre podporu pošlite screenshot tejto stránky
      </p>
    </div>
  );
}
